import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { env } from '../config/env';
import { formatClockTime, formatLongDate, greeting } from '../lib/date';
import { useDailyState } from '../lib/storage';
import { useWeather } from '../lib/weather/useWeather';
import { useTheme } from './ThemeProvider';
import { Pill } from './ui';

/** Ticks once a second. Only the top bar needs it, so date.ts stays React-free. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

export function TopBar() {
  const now = useNow();
  const { theme, toggle } = useTheme();

  // One Priority: my-hub:top:priority, resets at local midnight.
  // Explicitly NOT coupled to the Todos panel (spec.md is emphatic about this).
  const [priority, setPriority] = useDailyState<string>('top', 'priority', '');
  const [draft, setDraft] = useState(priority);
  const seeded = useRef(priority);

  // Re-seed the input when the day rolls over (useDailyState hands back '').
  useEffect(() => {
    if (seeded.current !== priority) {
      seeded.current = priority;
      setDraft(priority);
    }
  }, [priority]);

  // Debounce: one localStorage write per pause, not one per keystroke.
  useEffect(() => {
    if (draft === priority) return;
    const id = window.setTimeout(() => setPriority(draft), 250);
    return () => window.clearTimeout(id);
  }, [draft, priority, setPriority]);

  return (
    <header
      className="flex h-14 shrink-0 items-center gap-4 border-b border-line bg-panel px-4"
      data-testid="top-bar"
    >
      <div className="flex shrink-0 items-baseline gap-2">
        <span className="truncate text-[15px] font-semibold text-ink" data-testid="greeting">
          {greeting(env.displayName, now)}
        </span>
        <span className="text-ink-subtle" aria-hidden>
          ·
        </span>
        <span className="whitespace-nowrap text-sm text-ink-muted">{formatLongDate(now)}</span>
        <span className="text-ink-subtle" aria-hidden>
          ·
        </span>
        <time className="whitespace-nowrap text-sm font-medium tabular-nums text-ink-muted">
          {formatClockTime(now)}
        </time>
      </div>

      {/* weather-cal's hook is third-party code from the top bar's view. The
          per-slot PanelBoundary does not cover the header, so isolate it here. */}
      <ErrorBoundary
        fallback={<span className="shrink-0 text-xs text-ink-subtle">weather —</span>}
      >
        <WeatherSummary />
      </ErrorBoundary>

      <label className="ml-auto flex min-w-0 flex-1 items-center gap-2">
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
          Today
        </span>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="One priority for today…"
          maxLength={120}
          data-testid="priority-input"
          className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-subtle focus:border-accent focus:outline-none"
        />
      </label>

      <button
        type="button"
        onClick={toggle}
        data-testid="theme-toggle"
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        className="shrink-0 rounded-lg border border-line px-2.5 py-1.5 text-sm text-ink-muted transition-colors hover:bg-panel-raised"
      >
        {theme === 'dark' ? '☀' : '☾'}
      </button>
    </header>
  );
}

function WeatherSummary() {
  const { status, now, hasSevereAlert, alerts } = useWeather();

  if (status !== 'ready' || now === null) {
    return (
      <span className="shrink-0 text-xs text-ink-subtle" data-testid="topbar-weather">
        {status === 'unconfigured'
          ? `${env.weather.locationName} · weather not configured`
          : status === 'error'
            ? 'weather —'
            : '…'}
      </span>
    );
  }

  const temp = env.weather.units === 'metric' ? now.tempC : now.tempF;
  const unit = env.weather.units === 'metric' ? '°C' : '°F';

  return (
    <div className="flex shrink-0 items-center gap-2" data-testid="topbar-weather">
      <span className="text-base leading-none" aria-hidden>
        {now.icon}
      </span>
      <span className="text-sm font-medium tabular-nums text-ink">
        {Math.round(temp)}
        {unit}
      </span>
      <span className="truncate text-sm text-ink-muted">{now.conditionLabel}</span>
      {hasSevereAlert && (
        <Pill
          tone="alert"
          className="animate-pulse"
          testId="severe-alert"
        >
          ⚠ {alerts[0]?.event ?? 'Alert'}
        </Pill>
      )}
    </div>
  );
}
